// src/pages/admin/AdminSupport.tsx
import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container, Box, Typography, Paper, TextField, IconButton, Avatar,
  List, ListItem, ListItemButton, ListItemAvatar, ListItemText, Divider, Badge,
  CircularProgress, Alert, useTheme, alpha, Chip
} from '@mui/material';
import {
  Send as SendIcon,
  ArrowBack as ArrowBackIcon,
  Support as SupportIcon
} from '@mui/icons-material';
import {
  getAllSupportConversations,
  getUserSupportConversation,
  replySupportMessage,
  type SupportConversation,
  type SupportMessage
} from '../../services/support';

const AdminSupport = () => {
  const { userId: selectedUserId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const [conversations, setConversations] = useState<SupportConversation[]>([]);
  const [messages, setMessages] = useState<SupportMessage[]>([]);
  const [messageText, setMessageText] = useState('');
  const [loading, setLoading] = useState(true);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [selectedConversation, setSelectedConversation] = useState<SupportConversation | null>(null);

  useEffect(() => {
    loadConversations();
  }, []);

  useEffect(() => {
    if (selectedUserId) {
      loadMessages(selectedUserId);
      const conv = conversations.find(c => c.user.id === selectedUserId);
      setSelectedConversation(conv || null);
    }
  }, [selectedUserId, conversations]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const loadConversations = async () => {
    try {
      const data = await getAllSupportConversations();
      setConversations(data);
    } catch (error) {
      console.error('Error loading support conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadMessages = async (userId: string) => {
    try {
      const data = await getUserSupportConversation(userId);
      setMessages(data);
    } catch (error) {
      console.error('Error loading support messages:', error);
    }
  };

  const handleSendMessage = async () => {
    if (!messageText.trim() || !selectedUserId || sendingMessage) return;

    setSendingMessage(true);
    try {
      const newMessage = await replySupportMessage(selectedUserId, messageText.trim());
      setMessages([...messages, newMessage]);
      setMessageText('');
      // Refresh conversations to update last message
      await loadConversations();
    } catch (error: any) {
      console.error('Error sending message:', error);
      alert('Failed to send message');
    } finally {
      setSendingMessage(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSelectConversation = (userId: string) => {
    navigate(`/admin/support/${userId}`);
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
        <SupportIcon sx={{ fontSize: 40, color: theme.palette.primary.main }} />
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          Support Messages
        </Typography>
      </Box>

      <Paper
        elevation={3}
        sx={{
          height: 'calc(100vh - 200px)',
          display: 'flex',
          borderRadius: 4,
          overflow: 'hidden',
          background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.95)}, ${alpha(theme.palette.background.paper, 1)})`,
          backdropFilter: 'blur(10px)',
          border: `1px solid ${alpha(theme.palette.divider, 0.1)}`
        }}
      >
        {/* Conversations List */}
        <Box
          sx={{
            width: { xs: '100%', md: 360 },
            borderRight: { md: `1px solid ${alpha(theme.palette.divider, 0.1)}` },
            display: { xs: selectedUserId ? 'none' : 'block', md: 'block' },
            overflowY: 'auto',
            background: alpha(theme.palette.background.default, 0.3),
            '&::-webkit-scrollbar': {
              width: '8px',
            },
            '&::-webkit-scrollbar-track': {
              background: alpha(theme.palette.divider, 0.05),
            },
            '&::-webkit-scrollbar-thumb': {
              background: alpha(theme.palette.primary.main, 0.3),
              borderRadius: '4px',
              '&:hover': {
                background: alpha(theme.palette.primary.main, 0.5),
              },
            },
          }}
        >
          {conversations.length === 0 ? (
            <Box sx={{ p: 3 }}>
              <Alert severity="info">
                No support conversations yet
              </Alert>
            </Box>
          ) : (
            <List sx={{ p: 1 }}>
              {conversations.map((conversation) => (
                <ListItemButton
                  key={conversation.user.id}
                  selected={selectedUserId === conversation.user.id}
                  onClick={() => handleSelectConversation(conversation.user.id)}
                  sx={{
                    borderRadius: 2,
                    mb: 0.5,
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      transform: 'translateX(4px)',
                      bgcolor: alpha(theme.palette.primary.main, 0.08),
                    },
                    '&.Mui-selected': {
                      bgcolor: alpha(theme.palette.primary.main, 0.15),
                      borderLeft: `3px solid ${theme.palette.primary.main}`,
                      '&:hover': {
                        bgcolor: alpha(theme.palette.primary.main, 0.2),
                      }
                    }
                  }}
                >
                  <ListItemAvatar>
                    <Badge
                      badgeContent={conversation.unreadCount}
                      color="error"
                      overlap="circular"
                      sx={{
                        '& .MuiBadge-badge': {
                          animation: conversation.unreadCount > 0 ? 'pulse 2s infinite' : 'none',
                          '@keyframes pulse': {
                            '0%': { transform: 'scale(1)' },
                            '50%': { transform: 'scale(1.1)' },
                            '100%': { transform: 'scale(1)' },
                          }
                        }
                      }}
                    >
                      <Avatar
                        src={conversation.user.photoUrl || ''}
                        alt={conversation.user.firstName}
                        sx={{
                          width: 48,
                          height: 48,
                          border: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`
                        }}
                      />
                    </Badge>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography
                          variant="subtitle2"
                          sx={{
                            fontWeight: conversation.unreadCount > 0 ? 700 : 500,
                            color: conversation.unreadCount > 0 ? theme.palette.text.primary : theme.palette.text.secondary
                          }}
                        >
                          {`${conversation.user.firstName} ${conversation.user.lastName}`}
                        </Typography>
                        <Chip
                          label={conversation.user.userType}
                          size="small"
                          sx={{ height: 20, fontSize: '0.7rem' }}
                        />
                      </Box>
                    }
                    secondary={
                      conversation.lastMessage ? (
                        <Typography
                          variant="body2"
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            fontWeight: conversation.unreadCount > 0 ? 600 : 400,
                            color: conversation.unreadCount > 0 ? theme.palette.text.primary : theme.palette.text.secondary
                          }}
                        >
                          {conversation.lastMessage.isFromAdmin ? '✓ You: ' : ''}
                          {conversation.lastMessage.message}
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="text.disabled">
                          No messages yet
                        </Typography>
                      )
                    }
                  />
                </ListItemButton>
              ))}
            </List>
          )}
        </Box>

        {/* Chat Area */}
        <Box
          sx={{
            flex: 1,
            display: { xs: selectedUserId ? 'flex' : 'none', md: 'flex' },
            flexDirection: 'column'
          }}
        >
          {!selectedUserId ? (
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                p: 4
              }}
            >
              <Typography variant="h6" color="text.secondary">
                Select a conversation to start messaging
              </Typography>
            </Box>
          ) : (
            <>
              {/* Chat Header */}
              <Box
                sx={{
                  p: 2.5,
                  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.03)}, ${alpha(theme.palette.background.paper, 0.8)})`,
                  backdropFilter: 'blur(10px)',
                }}
              >
                <IconButton
                  sx={{
                    display: { md: 'none' },
                    bgcolor: alpha(theme.palette.background.paper, 0.8),
                    '&:hover': {
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                    }
                  }}
                  onClick={() => navigate('/admin/support')}
                >
                  <ArrowBackIcon />
                </IconButton>
                {selectedConversation && (
                  <>
                    <Avatar
                      src={selectedConversation.user.photoUrl || ''}
                      alt={selectedConversation.user.firstName}
                      sx={{
                        width: 50,
                        height: 50,
                        border: `3px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                        boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`
                      }}
                    />
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.25 }}>
                        {selectedConversation.user.firstName} {selectedConversation.user.lastName}
                      </Typography>
                      <Typography variant="caption" sx={{ color: alpha(theme.palette.text.secondary, 0.8) }}>
                        {selectedConversation.user.email} • {selectedConversation.user.userType}
                      </Typography>
                    </Box>
                  </>
                )}
              </Box>

              {/* Messages Area */}
              <Box
                sx={{
                  flex: 1,
                  overflowY: 'auto',
                  p: 3,
                  background: `radial-gradient(ellipse at top, ${alpha(theme.palette.primary.main, 0.03)}, transparent 70%),
                              radial-gradient(ellipse at bottom, ${alpha(theme.palette.secondary.main, 0.02)}, transparent 70%)`,
                  '&::-webkit-scrollbar': {
                    width: '8px',
                  },
                  '&::-webkit-scrollbar-track': {
                    background: alpha(theme.palette.divider, 0.05),
                  },
                  '&::-webkit-scrollbar-thumb': {
                    background: alpha(theme.palette.primary.main, 0.3),
                    borderRadius: '4px',
                    '&:hover': {
                      background: alpha(theme.palette.primary.main, 0.5),
                    },
                  },
                }}
              >
                {messages.length === 0 ? (
                  <Box sx={{ textAlign: 'center', mt: 8 }}>
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                      No messages yet
                    </Typography>
                    <Typography variant="body2" color="text.disabled">
                      Start the conversation with the user
                    </Typography>
                  </Box>
                ) : (
                  messages.map((message, index) => {
                    const isSentByAdmin = message.isFromAdmin;
                    const showDate = index === 0 ||
                      new Date(messages[index - 1].createdAt).toDateString() !== new Date(message.createdAt).toDateString();

                    return (
                      <Box key={message.id}>
                        {showDate && (
                          <Box sx={{ textAlign: 'center', my: 2 }}>
                            <Chip
                              label={new Date(message.createdAt).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric'
                              })}
                              size="small"
                              sx={{
                                bgcolor: alpha(theme.palette.background.paper, 0.8),
                                backdropFilter: 'blur(10px)',
                                fontSize: '0.75rem'
                              }}
                            />
                          </Box>
                        )}
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: isSentByAdmin ? 'flex-end' : 'flex-start',
                            mb: 1.5,
                            animation: 'slideIn 0.3s ease-out',
                            '@keyframes slideIn': {
                              from: {
                                opacity: 0,
                                transform: isSentByAdmin ? 'translateX(20px)' : 'translateX(-20px)'
                              },
                              to: {
                                opacity: 1,
                                transform: 'translateX(0)'
                              }
                            }
                          }}
                        >
                          <Box
                            sx={{
                              maxWidth: '70%',
                              position: 'relative'
                            }}
                          >
                            <Box
                              sx={{
                                bgcolor: isSentByAdmin
                                  ? `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`
                                  : alpha(theme.palette.background.paper, 0.95),
                                background: isSentByAdmin
                                  ? `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`
                                  : alpha(theme.palette.background.paper, 0.95),
                                color: isSentByAdmin ? 'white' : 'text.primary',
                                p: 1.5,
                                px: 2,
                                borderRadius: 3,
                                borderTopRightRadius: isSentByAdmin ? 4 : 16,
                                borderTopLeftRadius: isSentByAdmin ? 16 : 4,
                                borderBottomRightRadius: isSentByAdmin ? 4 : 16,
                                borderBottomLeftRadius: isSentByAdmin ? 16 : 4,
                                boxShadow: isSentByAdmin
                                  ? `0 2px 12px ${alpha(theme.palette.primary.main, 0.3)}`
                                  : `0 2px 12px ${alpha(theme.palette.grey[500], 0.15)}`,
                                border: isSentByAdmin ? 'none' : `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                                backdropFilter: 'blur(10px)',
                                transition: 'transform 0.2s ease-in-out',
                                '&:hover': {
                                  transform: 'scale(1.02)'
                                }
                              }}
                            >
                              <Typography variant="body1" sx={{ wordBreak: 'break-word', lineHeight: 1.5 }}>
                                {message.message}
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{
                                  display: 'block',
                                  mt: 0.5,
                                  opacity: 0.7,
                                  fontSize: '0.7rem',
                                  textAlign: 'right'
                                }}
                              >
                                {new Date(message.createdAt).toLocaleTimeString([], {
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                      </Box>
                    );
                  })
                )}
                <div ref={messagesEndRef} />
              </Box>

              {/* Message Input */}
              <Box
                sx={{
                  p: 2.5,
                  borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                  background: `linear-gradient(180deg, ${alpha(theme.palette.background.paper, 0.8)}, ${alpha(theme.palette.background.paper, 1)})`,
                  backdropFilter: 'blur(10px)',
                }}
              >
                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-end' }}>
                  <TextField
                    fullWidth
                    placeholder="Type a message..."
                    value={messageText}
                    onChange={(e) => setMessageText(e.target.value)}
                    onKeyPress={handleKeyPress}
                    disabled={sendingMessage}
                    multiline
                    maxRows={4}
                    variant="outlined"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 3,
                        bgcolor: alpha(theme.palette.background.default, 0.5),
                        transition: 'all 0.3s ease',
                        '& fieldset': {
                          borderColor: alpha(theme.palette.divider, 0.2),
                          borderWidth: 1.5,
                        },
                        '&:hover fieldset': {
                          borderColor: alpha(theme.palette.primary.main, 0.3),
                        },
                        '&.Mui-focused': {
                          bgcolor: alpha(theme.palette.background.paper, 0.95),
                          boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.1)}`,
                          '& fieldset': {
                            borderColor: theme.palette.primary.main,
                            borderWidth: 2,
                          }
                        }
                      },
                      '& .MuiInputBase-input': {
                        fontSize: '0.95rem',
                        lineHeight: 1.6,
                      }
                    }}
                  />
                  <IconButton
                    onClick={handleSendMessage}
                    disabled={!messageText.trim() || sendingMessage}
                    sx={{
                      width: 48,
                      height: 48,
                      background: !messageText.trim() || sendingMessage
                        ? alpha(theme.palette.grey[400], 0.3)
                        : `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                      color: 'white',
                      boxShadow: !messageText.trim() || sendingMessage
                        ? 'none'
                        : `0 4px 16px ${alpha(theme.palette.primary.main, 0.4)}`,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: !messageText.trim() || sendingMessage ? 'none' : 'translateY(-2px) scale(1.05)',
                        boxShadow: !messageText.trim() || sendingMessage
                          ? 'none'
                          : `0 6px 20px ${alpha(theme.palette.primary.main, 0.5)}`,
                        background: !messageText.trim() || sendingMessage
                          ? alpha(theme.palette.grey[400], 0.3)
                          : `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                      },
                      '&:active': {
                        transform: !messageText.trim() || sendingMessage ? 'none' : 'scale(0.95)',
                      },
                      '&:disabled': {
                        color: alpha(theme.palette.grey[500], 0.5)
                      }
                    }}
                  >
                    {sendingMessage ? (
                      <CircularProgress size={24} sx={{ color: 'white' }} />
                    ) : (
                      <SendIcon />
                    )}
                  </IconButton>
                </Box>
              </Box>
            </>
          )}
        </Box>
      </Paper>
    </Container>
  );
};

export default AdminSupport;
